/* eslint-disable react/no-unknown-property */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Button, Divider, Skeleton, Stack, Text } from '@mantine/core';
import { useLingui } from '@lingui/react';
import {
  IconCheck,
  IconCopy,
  IconDownload,
  IconMailForward,
  IconPrinter,
  IconSettings,
  IconShare,
} from '@tabler/icons';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { useClipboard } from '@mantine/hooks';
import Moment from 'react-moment';
import { downloadDocumentContractQuestionnaire } from '../../Service/DocumentService';
import { ToastS } from '../../Service/ToastS';
import { EventPortalContext } from '../../Service/Context/EventPortalContext';
import { fetchResourceByEntityTypeAndEntityId, newShare, share } from '../../Service/restapi/shareService';
import ImportButton from '../Lexoffice/ImportButton';
import { PdfS } from '../../Service/PdfS';
import { DocumentS } from '../../Templates/Documents/DocumentS';
import { ViolationContext } from '../../Service/Context/ViolationContext';
import { LicenceS } from '../../Service/LicenceS';
import { PortalResouce } from '../../Types/ShareT';
import SevdeskSyncButton from '../Event/Documents/SevdeskSyncButton';
import EntityPreviewFooter from '../../Templates/Navigation/phone/EntityPreviewFooter';
import EmailImportButton from '../Lexoffice/EmailImportButton';
import { ProfileContext } from '../../Service/Context/ProfileContext';

const DocumentPreviewActions: FC<{
  entityType: string;
  entity: any;
  documentFile?: Blob;
  dropDownVesion?: boolean;
  portalSupported?: boolean;
  setLastModified: (d: Date) => void;
  patchEntity?: (patch: any, onSuccess?: () => void) => void;
  isPrintable?: boolean;
  editDocument: () => void;
  handleChange: (patch: any) => void;
}> = ({
  entityType,
  entity,
  documentFile,
  dropDownVesion,
  portalSupported,
  setLastModified,
  patchEntity,
  isPrintable = true,
  editDocument,
  handleChange,
}) => {
  const navigate: NavigateFunction = useNavigate();
  const { profile } = useContext(ProfileContext);
  const { i18n } = useLingui();
  const clipboard = useClipboard({ timeout: 1000 });
  const { shares, reload } = useContext(EventPortalContext);
  const { executeWithLicenceCheck, interceptViolation } = useContext(ViolationContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [portalResource, setPortalResource] = useState<PortalResouce>();
  const handleChangeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const { id, eventId, documentType, draft, label, xrechnungRequired } = entity;
  const isInvoice = documentType && DocumentS.isInvoice(documentType);
  const { accountingTool } = profile ?? {};

  useEffect(() => {
    if (portalSupported) {
      fetchPortalResource();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (clipboard.copied) {
      ToastS.info('linkCopied', 'Verknüpfung kopiert');
    }
  }, [clipboard.copied]);

  useEffect(() => {
    return () => {
      if (handleChangeTimeoutRef.current) {
        clearTimeout(handleChangeTimeoutRef.current);
      }
    };
  }, []);

  const handleChangeWithDelay = (patch: any) => {
    const shouldDelayHandleChange = documentType === 'OFFER' || entityType === 'contracts';

    if (!shouldDelayHandleChange) {
      handleChange(patch);
      return;
    }

    if (handleChangeTimeoutRef.current) {
      clearTimeout(handleChangeTimeoutRef.current);
    }

    handleChangeTimeoutRef.current = setTimeout(() => {
      handleChange(patch);
    }, 1000);
  };

  const createShare = () => {
    setLoading(true);
    const requestBody = [
      { entityType, entityId: id, label: label ?? i18n._(`resource.entityType.${entityType.toLowerCase()}`) },
    ];

    if (shares.length > 0) {
      share(shares[0].id, { entityTypeAndId: requestBody }, entity)
        .then((resp) => {
          if (resp.ok) {
            fetchPortalResource();
            let patch: any = { draft: false };
            // Trigger PDF update only for offers and contracts
            if (documentType === 'OFFER' || entityType === 'contracts') {
              patch.lastModified = new Date();
            }
            handleChangeWithDelay(patch);
          }
        })
        .finally(() => setLoading(false));
    } else {
      newShare(undefined, undefined, undefined, eventId).then((resp) => {
        if (resp.ok) {
          resp.json().then((s) => {
            share(s.id, { entityTypeAndId: requestBody })
              .then((resp) => {
                if (resp.ok) {
                  fetchPortalResource();
                  let patch: any = { draft: false };
                  // Trigger PDF update only for offers and contracts
                  if (documentType === 'OFFER' || entityType === 'contracts') {
                    patch.lastModified = new Date();
                  }
                  handleChangeWithDelay(patch);
                }
              })
              .finally(() => {
                reload();
                setLoading(false);
              });
          });
        }
      });
    }
  };

  const fetchPortalResource = () => {
    setLoading(true);
    fetchResourceByEntityTypeAndEntityId(entityType, id)
      .then((resp) => {
        if (resp.ok) {
          resp.json().then((respJSON: PortalResouce[]) => {
            if (respJSON.length > 0) {
              setPortalResource(respJSON[0]);
            }
          });
        }
      })
      .finally(() => setLoading(false));
  };

  const printDocument = () => {
    if (documentFile) {
      PdfS.printDocument(documentFile);
    } else {
      ToastS.info('loading-document', 'Datei laden');
    }
  };
  const downloadDocument = () =>
    downloadDocumentContractQuestionnaire(
      entityType,
      id,
      i18n,
      () => patchEntity && patchEntity({ draft: false }),
      interceptViolation,
    );

  const sentDocumentByMail = () => {
    if (eventId) {
      navigate(`/events/${eventId}/emails/inbox?entityType=${entityType}&entityId=${id}`);
    } else {
      navigate(`/postbox/inbox?entityType=${entityType}&entityId=${id}`);
    }
  };

  const markAsReady = (withRedirect = true) => {
    if (patchEntity) {
      patchEntity({ draft: false }, () => {
        ToastS.success('document-finished', 'Dokument wurde erfolgreich abgeschlossen');
        if (withRedirect) {
          if (eventId) {
            navigate(`/events/${eventId}/documents`);
          } else {
            navigate(`/documents`);
          }
        }
      });
    }
  };

  const publish = () => executeWithLicenceCheck(LicenceS.Restrictions.PORTAL, createShare);

  const downloadXRechnung = () => PdfS.generateXRechnungXML(entity.id, entity.fileName, interceptViolation);

  return (
    <div>
      {dropDownVesion && (
        <EntityPreviewFooter
          context={{
            portalSupported: portalSupported ?? false,
            isPrintable,
            published: portalResource !== undefined,
            portalLink: portalResource ? portalResource.digitalSignLink : null,
            editable: draft,
            entity,
            isInvoice,
          }}
          handlers={{
            publish,
            sendViaEmail: sentDocumentByMail,
            printDocument,
            downloadDocument,
            setLastModified,
            editDocument,
            markAsReady: isPrintable && draft && patchEntity ? markAsReady : undefined,
          }}
        />
      )}

      {!dropDownVesion && (
        <Stack
          ml={20}
          pb={10}
          sx={(theme) => ({
            border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[1]}`,
            borderRadius: 10,
            minWidth: 275,
          })}
        >
          <Text
            p="md"
            size="md"
            weight="bolder"
            sx={(theme) => ({
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.lightBlue[0],
              color: theme.colorScheme === 'dark' ? 'white' : theme.colors.blue[3],
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
            })}
          >
            Dokument bereitstellen
          </Text>
          {documentType && DocumentS.isInvoice(documentType) && (
            <>
              {accountingTool === 'LexOffice' && (
                <ImportButton bookingDocument={entity} setLastModified={setLastModified} />
              )}
              {accountingTool === 'sevDesk' && (
                <SevdeskSyncButton bookingDocument={entity} setLastModified={setLastModified} />
              )}
              {(accountingTool === 'E-Mail' || accountingTool === 'Accountable' || accountingTool === 'DATEV') && (
                <EmailImportButton bookingDocument={entity} />
              )}
              {!accountingTool ||
                (!['LexOffice', 'sevDesk', 'E-Mail', 'Accountable', 'DATEV'].includes(accountingTool) && (
                  <>
                    <div>
                      <IconSettings color="gray" style={{ marginLeft: 15, marginRight: 15 }} />
                      <Button onClick={() => navigate('/settings/bookkeeping')}>Buchhaltung einrichten</Button>
                    </div>
                    <Divider />
                  </>
                ))}
            </>
          )}
          {portalSupported && eventId && (
            <div>
              {loading ? (
                <Skeleton width="80%" height={40} ml={25} mt="sm" mb="sm" />
              ) : (
                <div>
                  {portalResource ? (
                    <>
                      <IconCopy color="gray" style={{ marginLeft: 15, marginRight: 15 }} />
                      <Button onClick={() => clipboard.copy(portalResource.digitalSignLink)}>
                        {clipboard.copied ? 'Verknüpfung kopiert' : 'Portal-Link kopieren'}
                      </Button>
                      <Text align="center" size="xs" color="dimmed" mt="xs" mb="xs">
                        <IconCheck color="green" size={16} style={{ marginRight: 5 }} />
                        am <Moment date={portalResource.lastModified} format="DD.MM.YY" /> online freigegeben
                      </Text>
                    </>
                  ) : (
                    <>
                      <IconShare color="gray" style={{ marginLeft: 15, marginRight: 15 }} />
                      <Button mb="xs" onClick={publish}>
                        Online freigeben
                      </Button>
                    </>
                  )}
                </div>
              )}
              <Divider />
            </div>
          )}
          <div>
            <IconMailForward color="gray" style={{ marginLeft: 15, marginRight: 15 }} />
            <Button onClick={sentDocumentByMail}>E-Mail an Kunden</Button>
          </div>
          <Divider />
          <div>
            <IconDownload color="gray" style={{ marginLeft: 15, marginRight: 15 }} />
            <Button onClick={downloadDocument}>Herunterladen</Button>
          </div>
          <Divider />
          {/* {xrechnungRequired && (
            <>
              <div>
                <IconFileDownload color="gray" style={{ marginLeft: 15, marginRight: 15 }} />
                <Button onClick={downloadXRechnung}>XRechnung</Button>
              </div>
              <Divider />
            </>
          )} */}
          <div>
            <IconPrinter color="gray" style={{ marginLeft: 15, marginRight: 15 }} />
            <Button onClick={printDocument}>Drucken</Button>
          </div>
          {isPrintable && draft && patchEntity && (
            <>
              <Divider />
              <div>
                <IconCheck color="gray" style={{ marginLeft: 15, marginRight: 15 }} />
                <Button onClick={() => markAsReady()}>Fertigstellen</Button>
              </div>
            </>
          )}
        </Stack>
      )}
    </div>
  );
};

export default DocumentPreviewActions;
